import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  Textarea,
} from "@/components/ui";
import { DatePicker } from "@/components/ui/DatePicker";
import { toISODateOnlyLocal } from "@/lib/time";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams } from "@tanstack/react-router";
import { Controller, useForm } from "react-hook-form";
import { useCreateWorkNote, useSendWorkNote } from "./network/useWorkNotes";
import { type WorkNoteFormValues, workNoteSchema } from "./schema";

interface WorkNoteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WorkNoteModal({ open, onOpenChange }: WorkNoteModalProps) {
  const { patientId } = useParams({ from: "/patient/$patientId" });

  const form = useForm<WorkNoteFormValues>({
    resolver: zodResolver(workNoteSchema),
    defaultValues: {
      visitDate: new Date(),
      excuseStartDate: null,
      excuseEndDate: null,
      returnToWorkDate: null,
      reason: "",
    },
  });

  const sendMutation = useSendWorkNote({ patientId });

  const createMutation = useCreateWorkNote({
    patientId,
    onSuccess: (workNoteId) => {
      form.reset();
      onOpenChange(false);
      sendMutation.mutate(workNoteId);
    },
  });

  const onSubmit = (values: WorkNoteFormValues) => {
    const payload = {
      patientId,
      dateIssued: toISODateOnlyLocal(new Date()) as string,
      visitDate: toISODateOnlyLocal(values.visitDate) as string,
      excuseStartDate: values.excuseStartDate
        ? toISODateOnlyLocal(values.excuseStartDate)
        : null,
      excuseEndDate: values.excuseEndDate
        ? toISODateOnlyLocal(values.excuseEndDate)
        : null,
      returnToWorkDate: values.returnToWorkDate
        ? toISODateOnlyLocal(values.returnToWorkDate)
        : null,
      reason: values.reason,
    };

    createMutation.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent className="w-full max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Work Note</DialogTitle>
            <DialogDescription className="hidden">
              Create a new work note
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4 pr-3"
          >
            <Controller
              control={form.control}
              name="visitDate"
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <DatePicker
                    label="Visit Date"
                    date={field.value}
                    setDate={field.onChange}
                    closeOnSelect
                  />
                  <FieldError errors={[fieldState.error]} />
                </Field>
              )}
            />

            <Controller
              control={form.control}
              name="excuseStartDate"
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <DatePicker
                    label="Excuse Start Date (optional)"
                    date={field.value ?? undefined}
                    setDate={field.onChange}
                    closeOnSelect
                  />
                  <FieldError errors={[fieldState.error]} />
                </Field>
              )}
            />

            <Controller
              control={form.control}
              name="excuseEndDate"
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <DatePicker
                    label="Excuse End Date (optional)"
                    date={field.value ?? undefined}
                    setDate={field.onChange}
                    closeOnSelect
                  />
                  <FieldError errors={[fieldState.error]} />
                </Field>
              )}
            />

            <Controller
              control={form.control}
              name="returnToWorkDate"
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <DatePicker
                    label="Return to Work Date (optional)"
                    date={field.value ?? undefined}
                    setDate={field.onChange}
                    closeOnSelect
                  />
                  <FieldError errors={[fieldState.error]} />
                </Field>
              )}
            />

            <Controller
              control={form.control}
              name="reason"
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor="reason">
                    Reason (internal use only)
                  </FieldLabel>
                  <Textarea
                    {...field}
                    id="reason"
                    rows={4}
                    aria-invalid={fieldState.invalid}
                    placeholder="Enter the reason for the work note"
                  />
                  <FieldError errors={[fieldState.error]} />
                </Field>
              )}
            />

            <DialogFooter>
              <Button
                size="sm"
                variant="secondary"
                type="button"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                type="submit"
                isLoading={createMutation.isPending || sendMutation.isPending}
              >
                Send to Patient Email
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
